// @flow
import React, { useMemo, useEffect, useCallback, useContext } from 'react';
import moment from 'moment-timezone';
import values from 'ramda/es/values';
import map from 'ramda/es/map';
import pipe from 'ramda/es/pipe';
import isEmpty from 'ramda/es/isEmpty';
import prop from 'ramda/es/prop';
import sort from 'ramda/es/sort';
import descend from 'ramda/es/descend';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PlainList from 'app/common/ui-next/plain-list';

import { StatementsContext } from './StatementsData';
import StatementsListLoader from './StatementsListLoader';
import StatementsListItem from './StatementsListItem';
import type { StatementType } from './StatementsData';


type Props = {
  currentStatementId: string | 'new',
  onCurrentStatementInvalidate: (newStatementId: number) => void,
  onStatementClick: (statementId: number) => void,
  disabled?: boolean,
}

const StatementsList = ({
  currentStatementId,
  onCurrentStatementInvalidate,
  onStatementClick,
  disabled = false,
}: Props) => {
  const timezone = useSelector(state => state.user.timezone, shallowEqual);
  const { t } = useTranslation('statements');


  const [
    [statements, loading, endOfList], {
      handleRequestNextStatements,
    },
  ] = useContext(StatementsContext);


  const statementsArr = useMemo(() => pipe(
    values,
    map(({ number, subject, created_at: createdAt }: StatementType) => ({
      id: number,
      number: `№ ${number}`,
      subject,
      createdAt: moment(createdAt).tz(timezone).format('DD.MM.YYYY HH:mm'),
      dateSortable: Date.parse(createdAt),
    })),
    sort(descend(prop('dateSortable')))
  )(statements), [statements, timezone]);


  const handleStatementClick = useCallback(({ id }: { +id: string, ... }) => {
    onStatementClick(parseInt(id, 10));
  }, [onStatementClick]);


  // реакция на невалидный currentStatementId
  // предок должен как то исправить эту ситуацию
  useEffect(() => {
    if (currentStatementId === 'new') return; // ничего не деалть, если создание формы
    if (statements && !isEmpty(statements) && !statements[currentStatementId]) {
      onCurrentStatementInvalidate(statementsArr[0].id);
    }
  }, [statementsArr, statements, currentStatementId]);


  const statementId = parseInt(currentStatementId, 10);

  return (
    <PlainList
      id="statements"
      ariaLabel={t('list-label')}
      onClick={handleStatementClick}
      autoHeight={false}
    >
      {statementsArr.map(({ id, number, subject, createdAt }) => (
        <StatementsListItem
          key={id}
          id={id}
          active={id === statementId}
          number={number}
          title={subject}
          date={createdAt}
          disabled={disabled}
        />
      ))}
      <StatementsListLoader
        loading={loading}
        onVisible={handleRequestNextStatements}
        disable={endOfList}
      />
    </PlainList>
  );
};

export default StatementsList;
